import { useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { paths } from '@/app/router/paths'
import { signOutOfficerAndLeave } from '@/lib/auth/officerSession'
import { Button } from '@/shared/ui/Button'
import { Dialog } from '@/shared/ui/Dialog'
import { useSessionStore } from '../store/sessionStore'

/**
 * "Reset Application" — the way back to step 1 from anywhere mid-journey.
 *
 * Distinct from `CancelApplicationButton`, which POSTs §8.1 and abandons the
 * application on the DHA side. This one is purely local: it drops the session
 * store and returns to the ID-number screen, so a citizen who mistyped an ID
 * number, or an officer handing the device to the next person in the queue, can
 * start clean without waiting on a network call that may fail.
 *
 * It occupies the header slot mid-journey; `CancelApplicationButton` takes that
 * same slot on the receipt (PublicLayout). So the server-side application is
 * LEFT OPEN on the DHA side when a citizen resets — `startApplication` resets
 * the store again the moment a new reference is minted, but nothing withdraws
 * the old one. That is the accepted trade for not blocking a restart on a
 * network call: a citizen who has actually SUBMITTED gets the real §8.1
 * withdrawal from the receipt instead.
 *
 * Confirmed rather than immediate: everything entered so far is unrecoverable
 * once cleared (§4.6/§4.7 answer 204 and there is no read-back endpoint — see
 * docs/api-gaps.md #4), so a slipped tap must not be able to wipe eight steps.
 *
 * Renders nothing before a reference number exists, because step 1 IS the
 * ID-number screen and there is nothing yet to reset. That guard is the
 * component's own; WHERE it is allowed to appear at all is a routing question
 * PublicLayout answers.
 */
export function ResetApplicationButton({ className }: { className?: string }) {
  const { t } = useTranslation('apply')
  const navigate = useNavigate()

  const appRefNo = useSessionStore((s) => s.appRefNo)
  const clear = useSessionStore((s) => s.clear)

  const [open, setOpen] = useState(false)

  /**
   * The comment that used to sit here claimed the router had already left the wizard
   * before RequireSession could re-render against an empty store, so the two could be
   * fired back to back. That is not true, and it is worth writing down why, because
   * four other exits copy this shape.
   *
   * `BrowserRouter` wraps its location update in `startTransition`, while zustand
   * goes through `useSyncExternalStore` — and react-dom schedules every external-store
   * re-render at SyncLane (`forceStoreRerender`) regardless of the calling context, so
   * wrapping the clear in a transition of our own cannot close the gap either. The
   * emptied store commits FIRST, the old wizard route renders against it, and
   * RequireSession fires a redirect that races the one below.
   *
   * The gap cannot be closed from here — `<BrowserRouter useTransitions={false}>`
   * would close it, at the cost of blanking every lazy route to the full-screen loader
   * on each navigation. So it is made HARMLESS instead, in WizardGuards.tsx: that
   * redirect now carries a stable `state` object and reads narrow selectors, so it
   * fires once rather than on every render, and it lands on `/apply`, which an empty
   * session resolves to this same destination. Worst case the citizen takes two extra
   * `replace` hops to the screen they were going to anyway. What made it an infinite
   * loop was never this handler; it was a resume that could send them back.
   *
   * The officer is signed out last, and out of band. The use case in the docstring
   * above — an officer handing the device to the next person in the queue — is
   * exactly the one where the SSO cache must not carry over: the next application
   * would otherwise open with the previous officer already "signed in", and be
   * recorded under their AgentID. Awaiting it here would hold this handler open, so
   * the local navigation goes first and the redirect to Microsoft supersedes it. A
   * no-op when SSO is off, which is what keeps the mock build and the Playwright
   * happy path unchanged.
   */
  const onConfirm = () => {
    setOpen(false)
    clear()
    void navigate(paths.apply.idNumber, { replace: true })
    void signOutOfficerAndLeave()
  }

  if (!appRefNo) return null

  return (
    <div className={className}>
      <Button
        variant="secondary"
        size="lg"
        // The header treatment, inherited from the Cancel button that used to
        // hold this slot (board 2173:30549): outlined in the action colour so it
        // reads as the one affordance to the right of the lockup. It no longer
        // sits under a step's primary CTA, so the quiet hairline styling it wore
        // at the bottom of the card would now under-weight it beside the logo.
        className="gap-0.75 border-action-text text-action-text hover:bg-primary-8 hover:text-action-text"
        onClick={() => setOpen(true)}
      >
        <RotateCcw aria-hidden="true" className="size-2" />
        {t('resetApplication.trigger')}
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={t('resetApplication.dialog.title')}
        description={t('resetApplication.dialog.body')}
      >
        <Button
          variant="secondary"
          className="rounded-btn border-hairline text-body2 hover:bg-page hover:text-ink-900"
          onClick={() => setOpen(false)}
        >
          {t('resetApplication.dialog.goBack')}
        </Button>
        <Button className="rounded-btn text-body2 font-semibold" onClick={onConfirm}>
          {t('resetApplication.dialog.confirm')}
        </Button>
      </Dialog>
    </div>
  )
}
